import { Injectable } from '@angular/core';
import { Validators, FormBuilder, FormControl, FormGroup } from '@angular/forms';
import { BehaviorSubject } from 'rxjs';
import {
    GlobalRegionService,
    CenterService,
    ZoneService,
    SubZoneTitleService,
    ProgramService,
    PlantService,
    ModelYearService,
} from '../../mesc/mesc.service';

import {
    ApplicationService,
    TntSystemService,
    TntService,
    TntToolTypeService,
} from '../tnt.service';

@Injectable()
export class ToolFilterManager {
    constructor(
        private formBuilder: FormBuilder,
        private globalRegionService: GlobalRegionService,
        private centerService: CenterService,
        private applicationService: ApplicationService,
        private systemService: TntSystemService,
        private toolTypeService: TntToolTypeService,
        private zoneService: ZoneService,
        private subZoneService: SubZoneTitleService,
        private programService: ProgramService,
        private plantService: PlantService,
        private modelYearService: ModelYearService,
        private filterService: TntService,
    ) {
        this.editForm.valueChanges
            .subscribe(x => {
                if (this.editForm.valid && !this.isLoading) {
                    filterService.post(this.editForm.value, '/tool/search')
                        .then(response => {
                            this.filterTitle.next('Filter Tools - ' + response.RecordCount + ' records found');
                            if (this.query) this.editForm.reset(this.query);
                            this.query = null;
                        })
                } else {
                    this.filterTitle.next('Filter Tools');
                    this.query = x;
                }
            })
        this.filterService.loading.subscribe(x => this.isLoading = x);
    }

    filterTitle: BehaviorSubject<string> = new BehaviorSubject<string>("Filter Tools");
    isLoading: boolean;
    private isIncludeDetail: boolean = false;
    private pageNumber: number = 1;
    filterState: any = {};
    lastFilterState: any = {};
    query: any;

    isFilterSet: boolean = false;
    template: any;
    isNewFilter: boolean;

    getFilters(state) {
        let page = state.skip ? (state.skip / state.take) + 1 : this.pageNumber;
        let orderBy: string = '';
        let sortOrder: string = '';

        if (this.isNewFilter) {
            this.isNewFilter = false;
            page = 1;
        }

        if (state.sort && state.sort.length && state.sort[0].dir) {
            orderBy = '&orderBy=' + state.sort[0].field;
            sortOrder = '&sortOrder=' + state.sort[0].dir;
        }

        let urlExtension = "tnt/tool/search?" +
            "pageSize=" + state.take +
            '&pageNumber=' + page +
            orderBy + sortOrder,
            fieldName = "TntTools";

        try {
            this.filterState.ToolsWithNoTeamCenterLink = false;

            return {
                filters: this.filterState,
                urlExtension: urlExtension,
                fieldName: fieldName
            }
        } catch (e) {
            return {
                filters: null,
                urlExtension: "tnt/Tools?" +
                    "pageSize=" + state.take +
                    '&pageNumber=' + page
                    + orderBy + sortOrder,
                fieldName: fieldName
            }
        }
    }

    validateFilterSet() {
        let state = this.toolFilterTemplate.editForm.value;
        this.isFilterSet = state.orderBy || state.includeDetail;
    }

    /**
     * Initialize Grid Filter
     */
    handleGridFilter = () => {
        this.template = this.toolFilterTemplate;
    }

    /**
     * Save selected filters
     */
    filter = (dataItem) => {
        let validFilter: boolean;
        this.isFilterSet = false;
        for (let k in dataItem) {
            if (dataItem[k]) {
                this.isFilterSet = true;
                this.isNewFilter = true;
                break;
            }
        }
        this.filterState = dataItem;
        this.lastFilterState = this.filterState;
        this.template = null;
    }

    /**
     * Restore selected filters after toggling off 'My Tools'
     */
    restore() {
        this.filterState = this.lastFilterState;
        this.isFilterSet = Object.keys(this.filterState).length > 0;
    }

    /**
     * Reset filters
     * @param force - set to true to remove previous filter states as well
     */
    reset(force: boolean = false) {
        if (this.isFilterSet) {
            this.lastFilterState = this.filterState;
        }
        if (force) {
            this.lastFilterState = {};
            this.editForm.reset();
            this.query = undefined;
        }
        this.template = null;
        this.filterState = {};
        this.isFilterSet = false;
    }

    cancel = () => {
        this.template = null;
    }


    /**
     * Add controls based on the type of Center selected
     * @param isBodyShop selected center type: true if equals 'Body Shop' false otherwise
     */
    updateEditForm(isBodyShop: boolean = false) {
        if (isBodyShop) {
            this.editForm.addControl('ZoneId', new FormControl());
            this.editForm.addControl('SubZoneId', new FormControl());
            this.editForm.get('SubZoneId').disable();
        } else {
            this.editForm.addControl('SystemId', new FormControl())
            this.editForm.addControl('ApplicationId', new FormControl())
            this.editForm.addControl('ToolTypeId', new FormControl())
            this.editForm.get('ApplicationId').disable();
            this.editForm.get('ToolTypeId').disable();
        }
    }


    /**
     * Reset the editForm controls based on the Center value
     * If a center is selected, call updateEditForm
     * @param CenterId: the Id of the selected Center
     */
    handleCenterChange = (CenterId: string = "") => {
        this.editForm.removeControl('SystemId');
        this.editForm.removeControl('ApplicationId');
        this.editForm.removeControl('ToolTypeId');
        this.editForm.removeControl('ZoneId');
        this.editForm.removeControl('SubZoneId');
        try {
            let center = this.centerService.data
                .find(center => CenterId === center.Id)
            let isBodyShop = center ? center.Description === 'Body Shop' : false;
            if (center) this.updateEditForm(isBodyShop);
            if (CenterId && !isBodyShop) {
                this.systemService.read('/Center/' + CenterId, true)
            }
        } catch (e) {
            console.error('HandleCenterChange Exception::', e);
        }
    }


    /**
     * If systemId is defined, query for applications under that system
     * otherwise reset application service to an empty list
     */
    handleSystemChange = (systemId: string) => {
        let appCtrl = this.editForm.get('ApplicationId');

        if (systemId) {
            this.applicationService.query('/System/' + systemId);
            appCtrl.enable();
        } else {
            appCtrl.disable();
            appCtrl.setValue(undefined);
            this.handleApplicationChange(undefined);
            this.applicationService.reset();
        }
    }


    /**
     * If appId is defined, query for ToolTypes under that application
     * otherwise reset ToolTypes service to an empty list
     */
    handleApplicationChange = (appId: string) => {
        let toolTypeCtrl = this.editForm.get('ToolTypeId');

        if (appId) {
            this.toolTypeService.query('/Application/' + appId);
            toolTypeCtrl.enable();
        } else {
            toolTypeCtrl.disable();
            toolTypeCtrl.setValue(undefined);
            this.toolTypeService.reset();
        }
    }


    /**
     * If zoneId is defined, query for SubZones under that Zone
     * otherwise reset Zones service to an empty list
     */
    handleZoneChange = (zoneId: string) => {
        let subZoneCtrl = this.editForm.get('SubZoneId');

        if (zoneId) {
            this.subZoneService.query(zoneId + '/subzonetitle', false)
            subZoneCtrl.enable();
        } else {
            subZoneCtrl.disable();
            subZoneCtrl.setValue(undefined);
            this.subZoneService.reset();
        }
    }

    handleClearFilter = () => {
        this.editForm.reset();
        this.query = undefined;
    }

    clearFilterControl = {
        type: "action",
        show: true,
        name: 'Clear',
        title: "Reset filter",
        iconClass: "fa fa-recycle fa-fw",
        callback: this.handleClearFilter,
    }

    editForm: FormGroup = this.formBuilder.group({
        CenterId: [],
        GlobalRegionId: [],
        PlantId: [],
        ProgramId: [],
        ModelYearId: [],
        OwnerFirstName: [],
        OwnerLastName: [],
        UsageOwnerLastName: [],
        CreatedFromDate: [],
        CreatedToDate: [],
        BlocksOfTools: [],
        SpecialHandTool: [],
        ToolNumber: [],
        Keywords: [],
        Clear: [],
    })

    formControls = [
        {
            type: "combobox",
            name: "GlobalRegionId",
            title: "Region",
            placeholder: "Select a region...",
            textField: "Description",
            valueField: "Id",
            service: this.globalRegionService,
        },
        this.clearFilterControl,
        {
            type: "combobox",
            name: "CenterId",
            title: "Center",
            onChange: this.handleCenterChange,
            placeholder: "Select a Center...",
            textField: "Description",
            valueField: "Id",
            service: this.centerService,
        },
        {
            type: "combobox",
            name: "SystemId",
            title: "System",
            placeholder: "Select a System...",
            textField: "Description",
            valueField: "Id",
            onChange: this.handleSystemChange,
            service: this.systemService,
        },
        {
            type: "combobox",
            name: "ApplicationId",
            title: "Application",
            placeholder: "Select an Application...",
            textField: "Description",
            valueField: "Id",
            onChange: this.handleApplicationChange,
            service: this.applicationService,
        },
        {
            type: "combobox",
            name: "ToolTypeId",
            title: "Tool Type",
            placeholder: "Select a Tool Type...",
            textField: "Description",
            valueField: "Id",
            service: this.toolTypeService,
        },
        {
            type: "combobox",
            name: "ZoneId",
            title: "Zone",
            placeholder: "Select a Zone...",
            textField: "Description",
            valueField: "Id",
            onChange: this.handleZoneChange,
            service: this.zoneService,
        },
        {
            type: "combobox",
            name: "SubZoneId",
            title: "Sub Zone",
            placeholder: "Select a Sub Zone...",
            textField: "SubZoneTitleDescription",
            valueField: "SubZoneId",
            service: this.subZoneService,
        },

        {
            type: "combobox",
            name: "ProgramId",
            title: "Program",
            placeholder: "Select a Program...",
            textField: "ProgramDescription",
            valueField: "Id",
            service: this.programService,
        },

        {
            type: "combobox",
            name: "PlantId",
            title: "Plant",
            placeholder: "Select a Plant...",
            textField: "PlantDescription",
            valueField: "Id",
            service: this.plantService,
        },
        {
            type: "combobox",
            name: "ModelYearId",
            title: "Model Year",
            placeholder: "Select a Model Year...",
            textField: "Year",
            valueField: "Id",
            service: this.modelYearService,
        },
        {
            type: "boolean",
            name: "BlocksOfTools",
            title: "Blocks Of Tools"
        },
        {
            type: 'date',
            name: 'CreatedFromDate',
            title: 'Tools Created From Date',
        },
        {
            type: "boolean",
            name: "SpecialHandTool",
            title: "Special / HandTool"
        },
        {
            type: 'date',
            name: 'CreatedToDate',
            title: ' Tools Created To Date',
        },
        {
            type: "text",
            name: "ToolNumber",
            title: "Tool Number",
        },
        {
            type: "text",
            name: "Keywords",
            title: "Keywords",
        },
        {
            type: "text",
            name: "OwnerFirstName",
            title: "Tool Owner First Name",
        },
        {
            type: "text",
            name: "OwnerLastName",
            title: "Tool Owner Last Name",
        },
        {
            type: "text",
            name: "UsageOwnerLastName",
            title: "Usage Owner Last Name",
        },
    ]

    toolFilterTemplate = {
        isNew: true,
        filter: true,
        customTitle: this.filterTitle,
        editForm: this.editForm,
        formControls: this.formControls,
    }

}
